/**
 * Calendar action system — the TYPES.
 *
 * A context menu is one VIEW of an action system, not the system itself; the
 * hotkey layer is another view of the same actions. Each action is a small
 * declarative object (see actions/*.ts) over two inputs:
 *
 *  - an ActionContext: a DISCRIMINATED UNION built once per interaction from
 *    the engine's MCEvent (see buildActionContext). `when` narrows on the
 *    union, so inside `run` the compiler PROVES the fields the action needs
 *    exist for the event kind it is shown on — the legacy menu's "is the data
 *    actually available?" runtime anxiety becomes a compile error.
 *
 *  - ActionServices: everything actions may touch, injected ONCE by the
 *    bridge. No 12-callback prop threading; no FullCalendar refs.
 *
 * Actions never own dialog JSX. They either execute a mutation or dispatch a
 * fully-payloaded dialog request into the store's activeCalendarDialog union
 * (rendered by the shell-wide CalendarActionDialogs host).
 */
import type { LucideIcon } from "lucide-react";
import { AppointmentDto } from "../../../api-client";
import type CalendarStore from "../../../stores/Calendar/calendarStore";
import { ActiveCalendarDialog } from "../../../stores/Calendar/calendarDialog.types";
import { PATIENT_SUBTABS } from "../../../stores/Patients/patientsStore";
import {
  CalendarEvent,
  ClientTimeSlot,
} from "../../Calendar/controller/adapters/types";
import { MCEvent } from "../model/types";

// ─── Context union ─────────────────────────────────────────────────────────

interface BaseCtx {
  /** The engine event (typed flags, kind, lane geometry). */
  event: MCEvent;
  /** The adapter event underneath — what legacy handlers consume. */
  raw: CalendarEvent;
}

/** Patient treatment slot (incl. group participants and continuous series). */
export interface AppointmentCtx extends BaseCtx {
  type: "appointment";
  slot: ClientTimeSlot;
  patientId: number;
  appointmentId?: string;
  isContinuous: boolean;
  isCancelled: boolean;
}

/** Named EventTimeSlot / ContinuousEventTimeSlot ("Weg", custom blocks…). */
export interface NamedEventCtx extends BaseCtx {
  type: "namedEvent";
  isContinuous: boolean;
  title: string;
}

/** Lunch break. */
export interface BreakCtx extends BaseCtx {
  type: "break";
}

/** Travel time (Weg as a real TravelTime slot). */
export interface TravelCtx extends BaseCtx {
  type: "travel";
}

/** Vacation / sickness / GoneFishing day blocks. */
export interface AbsenceCtx extends BaseCtx {
  type: "absence";
}

/** Cancelled day marker — reset restores the plan (deletes the exception). */
export interface CancelledDayCtx extends BaseCtx {
  type: "cancelledDay";
  exceptionId?: string;
}

/** Working-hours block; deletable only when it is an exception. */
export interface WorkingHoursCtx extends BaseCtx {
  type: "workingHours";
  exceptionId?: string;
}

/** Group-therapist umbrella event. */
export interface GroupCtx extends BaseCtx {
  type: "group";
  isContinuous: boolean;
  timeSlotId?: string;
  participantNames: ReadonlyArray<string>;
}

export type ActionContext =
  | AppointmentCtx
  | NamedEventCtx
  | BreakCtx
  | TravelCtx
  | AbsenceCtx
  | CancelledDayCtx
  | WorkingHoursCtx
  | GroupCtx;

export type ActionContextType = ActionContext["type"];

/**
 * Type-guard factory: `when: forType("appointment", (c) => !c.isCancelled)`
 * both filters AND narrows, so `run(ctx)` sees the concrete context type.
 */
export const forType =
  <T extends ActionContextType>(
    type: T,
    extra?: (ctx: Extract<ActionContext, { type: T }>) => boolean,
  ) =>
  (ctx: ActionContext): ctx is Extract<ActionContext, { type: T }> =>
    ctx.type === type &&
    (extra == null || extra(ctx as Extract<ActionContext, { type: T }>));

// ─── Services ──────────────────────────────────────────────────────────────

/**
 * Controller verbs the actions may invoke — the PROVEN mutation flows
 * (optimistic caches, pinboard bookkeeping) reused as-is from the shared
 * calendar controller. Accessed through a getter so actions never close over
 * a stale controller instance.
 */
export interface ActionControllerVerbs {
  onRemoveEvent: (timeslotId: string, event?: CalendarEvent) => Promise<void> | void;
  onRemoveContinuousEvent: (timeslotId: string) => Promise<void> | void;
  onRemoveGroupTherapist: (timeslotId: string) => Promise<void> | void;
  onMoveEventDate: (event: CalendarEvent) => Promise<void> | void;
  onOutdated: () => void;
}

export interface ActionServices {
  calendarStore: CalendarStore;
  controller: () => ActionControllerVerbs | null;
  lookupByAppointmentId: (appointmentId: string) => AppointmentDto | undefined;
  navigateToPatient: (
    patientId: number,
    rxId: string | null,
    subtab: PATIENT_SUBTABS,
  ) => void;
  deleteWorkHourException: (exceptionId: string) => Promise<unknown>;
  notify: (message: string, level: "info" | "warning" | "error") => void;
  openDialog: (dialog: ActiveCalendarDialog) => void;
}

// ─── Action ────────────────────────────────────────────────────────────────

export type ActionGroup = "navigate" | "modify" | "danger";

export interface CalendarAction<C extends ActionContext = ActionContext> {
  id: string;
  /** Menu section; groups render in declaration order with separators. */
  group: ActionGroup;
  icon: LucideIcon;
  /** Static or context-dependent label. */
  label: string | ((ctx: C) => string);
  /** Menu-local hotkey (single character, matched case-insensitively). */
  hotkey?: string;
  /** Visibility + type narrowing (build with forType). */
  when: (ctx: ActionContext) => ctx is C;
  run: (ctx: C, services: ActionServices) => void | Promise<void>;
}

export const actionLabel = (
  action: CalendarAction<never> | CalendarAction,
  ctx: ActionContext,
): string =>
  typeof action.label === "function"
    ? (action.label as (c: ActionContext) => string)(ctx)
    : action.label;
